import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  XCircle,
  Info,
  CheckCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react'
import { getTestResult } from '../services/api'
import type { TestResult, Issue, ImpactLevel, IssueType } from '../../../shared/types'
// import { exportReport } from '../services/api'
// import toast from 'react-hot-toast'
import LoadingAnimation from './LoadingAnimation'
import EnhancedFixDisplay from './EnhancedFixDisplay'


interface ScanResultsProps {
  scanId: string
}

interface IssueGroup {
  code: string;
  message: string;
  help: string;
  helpUrl?: string;
  impact: ImpactLevel;
  type: IssueType;
  wcagCriteria: string[];
  count: number;
  instances: Issue[];
}

export default function ScanResults({ scanId }: ScanResultsProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [showAllInstances, setShowAllInstances] = useState<Set<string>>(new Set())
  const [filterImpact, setFilterImpact] = useState<ImpactLevel | 'all'>('all')
  const [filterType, setFilterType] = useState<'error' | 'warning' | 'notice' | 'all'>('all')
  const [showPassedTests, setShowPassedTests] = useState(false)
  const [expandedPassedTests, setExpandedPassedTests] = useState<Set<string>>(new Set())

  const { data: testResult, isLoading } = useQuery<TestResult>({
    queryKey: ['testResult', scanId],
    queryFn: () => getTestResult(scanId),
  })

  // Group issues by code
  const groupIssuesByCode = (issues: Issue[]): IssueGroup[] => {
    const groups = new Map<string, IssueGroup>()
    
    for (const issue of issues) {
      if (!groups.has(issue.code)) {
        groups.set(issue.code, {
          code: issue.code,
          message: issue.message,
          help: issue.help,
          helpUrl: issue.helpUrl,
          impact: issue.impact,
          type: issue.type,
          wcagCriteria: issue.wcagCriteria,
          count: 0,
          instances: []
        })
      }
      
      const group = groups.get(issue.code)!
      group.count++
      group.instances.push(issue)
    }
    
    // Sort groups by count (descending)
    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  }

  const toggleGroup = (groupCode: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupCode)) {
      newExpanded.delete(groupCode)
      // Also remove from showAll when collapsing
      const newShowAll = new Set(showAllInstances)
      newShowAll.delete(groupCode)
      setShowAllInstances(newShowAll)
    } else {
      newExpanded.add(groupCode)
    }
    setExpandedGroups(newExpanded)
  }

  const toggleShowAll = (groupCode: string) => {
    const newShowAll = new Set(showAllInstances)
    if (newShowAll.has(groupCode)) {
      newShowAll.delete(groupCode)
    } else {
      newShowAll.add(groupCode)
    }
    setShowAllInstances(newShowAll)
  }

  // Export functionality commented out for now - will be implemented when export feature is ready
  // const handleExport = async (format: 'pdf' | 'html' | 'json' | 'csv') => {
  //   try {
  //     const blob = await exportReport(scanId, {
  //       format,
  //       includeScreenshots: true,
  //       includeCodeSnippets: true,
  //       template: 'technical',
  //     })
  //
  //     const url = URL.createObjectURL(blob)
  //     const a = document.createElement('a')
  //     a.href = url
  //     a.download = `accessibility-report-${scanId}.${format}`
  //     document.body.appendChild(a)
  //     a.click()
  //     document.body.removeChild(a)
  //     URL.revokeObjectURL(url)
  //
  //     toast.success(`Report exported as ${format.toUpperCase()}`)
  //   } catch (error) {
  //     toast.error('Failed to export report')
  //   }
  // }

  if (isLoading) {
    return <LoadingAnimation />
  }

  if (!testResult) {
    return null
  }

  const filteredIssues = testResult.issues.filter(issue => {
    if (filterImpact !== 'all' && issue.impact !== filterImpact) return false
    if (filterType !== 'all' && issue.type !== filterType) return false
    return true
  })
  
  // Group the filtered issues
  const issueGroups = groupIssuesByCode(filteredIssues)

  const getImpactColor = (impact: ImpactLevel) => {
    switch (impact) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200'
      case 'serious': return 'text-orange-600 bg-orange-50 border-orange-200'
      case 'moderate': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'minor': return 'text-blue-600 bg-blue-50 border-blue-200'
    }
  }

  const getTypeIcon = (type: Issue['type']) => {
    switch (type) {
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />
      case 'notice': return <Info className="w-5 h-5 text-blue-500" />
    }
  }

  const scoreColor = testResult.summary.score >= 90 
    ? 'text-green-600' 
    : testResult.summary.score >= 70 
    ? 'text-yellow-600' 
    : 'text-red-600'

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Test Results</h2>
            <p className="text-gray-600 mt-1">{testResult.url}</p>
          </div>
        </div>

        {/* Score and Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Accessibility Score</div>
            <div className={`text-3xl font-bold ${scoreColor}`}>
              {testResult.summary.score}/100
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Errors</div>
            <div className="text-3xl font-bold text-red-600">
              {testResult.summary.errors}
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Warnings</div>
            <div className="text-3xl font-bold text-yellow-600">
              {testResult.summary.warnings}
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Notices</div>
            <div className="text-3xl font-bold text-blue-600">
              {testResult.summary.notices}
            </div>
          </div>
        </div>

        {/* Test Info */}
        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">WCAG Level:</span>
            <span className="ml-2 font-medium">{testResult.wcagLevel}</span>
          </div>
          <div>
            <span className="text-gray-600">Tests Passed:</span>
            <span className="ml-2 font-medium">
              {testResult.summary.passedTests}/{testResult.summary.totalTests}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Page Title:</span>
            <span className="ml-2 font-medium">{testResult.metadata.pageTitle}</span>
          </div>
          <div>
            <span className="text-gray-600">Response Time:</span>
            <span className="ml-2 font-medium">{testResult.metadata.responseTime}ms</span>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setShowPassedTests(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                !showPassedTests 
                  ? 'bg-red-100 text-red-700 border border-red-300'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Issues ({testResult.issues.length})
              </div>
            </button>
            <button
              onClick={() => setShowPassedTests(true)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                showPassedTests 
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Passed Tests ({testResult.passedTests?.length || 0})
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Filters - Only show for issues */}
      {!showPassedTests && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Types</option>
            <option value="error">Errors Only</option>
            <option value="warning">Warnings Only</option>
            <option value="notice">Notices Only</option>
          </select>
          <select
            value={filterImpact}
            onChange={(e) => setFilterImpact(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Impact Levels</option>
            <option value="critical">Critical</option>
            <option value="serious">Serious</option>
            <option value="moderate">Moderate</option>
            <option value="minor">Minor</option>
          </select>
          <div className="ml-auto text-sm text-gray-600">
            Showing {issueGroups.length} issue types ({filteredIssues.length} total issues)
          </div>
        </div>
      </div>
      )}

      {/* Issues List or Passed Tests */}
      <div className="space-y-4">
        {!showPassedTests ? (
          // Show Grouped Issues
          issueGroups.length === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900">No Issues Found!</h3>
            <p className="text-green-700 mt-1">
              Great job! No accessibility issues match your current filters.
            </p>
          </div>
        ) : (
          issueGroups.map((group) => {
            const isExpanded = expandedGroups.has(group.code)
            const showingAll = showAllInstances.has(group.code)
            const instancesToShow = showingAll ? group.instances : group.instances.slice(0, 5)
            
            return (
              <div
                key={group.code}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.code)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {getTypeIcon(group.type)}
                    <div className="text-left">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{group.message}</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm font-bold rounded">
                          {group.count} {group.count === 1 ? 'issue' : 'issues'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getImpactColor(group.impact)}`}>
                          {group.impact}
                        </span>
                        <span className="text-xs text-gray-500">
                          {group.wcagCriteria.join(', ')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Group Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200">
                    {/* Issue Description */}
                    <div className="px-6 pt-4 pb-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-blue-900 mb-1">What to check:</h4>
                        <p className="text-sm text-blue-700">{group.help}</p>
                        {group.helpUrl && (
                          <a
                            href={group.helpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2 font-medium"
                          >
                            Learn more about this issue
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Enhanced Fix Recommendations */}
                    {group.instances[0]?.quickFix && (
                      <EnhancedFixDisplay issue={group.instances[0]} />
                    )}

                    {/* Affected Elements */}
                    <div className="px-6 pb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Affected Elements ({group.count}):
                      </h4>
                      <div className="space-y-2">
                        {instancesToShow.map((instance, idx) => (
                          <div key={instance.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-gray-500 font-mono mt-0.5">
                                {idx + 1}.
                              </span>
                              <div className="flex-1">
                                <code className="block text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">
                                  {instance.element}
                                </code>
                                <div className="mt-1">
                                  <code className="text-xs text-gray-500">
                                    {instance.selector}
                                  </code>
                                </div>
                                {instance.fixSuggestions && instance.fixSuggestions.length > 0 && (
                                  <div className="mt-2 text-xs text-gray-600 italic">
                                    Fix: {instance.fixSuggestions[0]}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Show More/Less Button */}
                      {group.count > 5 && (
                        <button
                          onClick={() => toggleShowAll(group.code)}
                          className="mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                        >
                          {showingAll ? (
                            <span className="flex items-center gap-2">
                              <ChevronUp className="w-4 h-4" />
                              Show less
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <ChevronDown className="w-4 h-4" />
                              Show all {group.count} elements
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )
      ) : (
        // Show Passed Tests
        <div className="space-y-4">
          {testResult.passedTests && testResult.passedTests.length > 0 ? (
            testResult.passedTests.map((test) => (
              <div
                key={test.id}
                className="bg-white rounded-lg shadow-sm border border-green-200 overflow-hidden"
              >
                <button
                  onClick={() => {
                    const newExpanded = new Set(expandedPassedTests)
                    if (newExpanded.has(test.id)) {
                      newExpanded.delete(test.id)
                    } else {
                      newExpanded.add(test.id)
                    }
                    setExpandedPassedTests(newExpanded)
                  }}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-green-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{test.description}</div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-green-600 font-medium">
                          ✓ {test.nodes} element{test.nodes !== 1 ? 's' : ''} passed
                        </span>
                        {test.wcagCriteria.length > 0 && (
                          <span className="text-xs text-gray-500">
                            {test.wcagCriteria.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedPassedTests.has(test.id) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedPassedTests.has(test.id) && (
                  <div className="px-6 pb-4 border-t border-green-200 bg-green-50">
                    <div className="mt-4 space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                        <p className="text-sm text-gray-600">{test.help}</p>
                      </div>
                      
                      {test.tags && test.tags.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-1">Tags</h4>
                          <div className="flex flex-wrap gap-1">
                            {test.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <Info className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-700">No Passed Tests Data</h3>
              <p className="text-gray-600 mt-1">
                Passed tests information is not available for this scan.
              </p>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  )
}
